import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../App';
import { Bell, CheckCheck, Sparkles } from 'lucide-react';

const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const MyNotificationsPage = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api().get('/student/me/notifications');
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch { /* silent */ }
    setLoading(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api().post(`/student/me/notifications/${id}/read`);
      setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch { /* silent */ }
  };

  return (
    <div className="space-y-5" data-testid="my-notifications-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
          <Bell className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1A2A4F]">Notifications</h1>
          <p className="text-sm text-gray-600">Updates about your applications, documents, and offers.</p>
        </div>
      </div>

      {loading ? (
        <p className="text-center py-10 text-gray-500" data-testid="notifs-loading">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-10 text-center" data-testid="notifs-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
            <Sparkles className="w-6 h-6" />
          </div>
          <h3 className="mt-4 font-bold text-[#1A2A4F]">You&apos;re all caught up</h3>
          <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">
            New updates about your application will show up here.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden" data-testid="notifs-table-wrap">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="notifs-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-bold text-[#1A2A4F]/70 w-10"></th>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-bold text-[#1A2A4F]/70">Title</th>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-bold text-[#1A2A4F]/70">Message</th>
                  <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-bold text-[#1A2A4F]/70">Received</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((n) => (
                  <tr key={n.id} className={n.read ? 'hover:bg-gray-50' : 'bg-[#F5A623]/5 hover:bg-[#F5A623]/10'} data-testid={`notif-${n.id}`}>
                    <td className="px-4 py-3">
                      <span className={`block w-2 h-2 rounded-full ${n.read ? 'bg-gray-300' : 'bg-[#F5A623]'}`} aria-hidden />
                    </td>
                    <td className="px-4 py-3 font-semibold text-[#1A2A4F]">{n.title || n.subject || 'Update'}</td>
                    <td className="px-4 py-3 text-gray-700 max-w-md">
                      <span className="line-clamp-2">{n.body || '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmt(n.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {!n.read ? (
                        <button onClick={() => markRead(n.id)} className="inline-flex items-center gap-1 text-[#F5A623] font-semibold text-xs hover:underline" data-testid={`notif-mark-read-${n.id}`}>
                          <CheckCheck className="w-3.5 h-3.5" /> Mark read
                        </button>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider text-gray-400">Read</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyNotificationsPage;
